/**
 * InfoTip — DESC-CLEAN-01 (Seth 2026-09-24: "Remove them if we don't need them
 * or put them in little 'I' type buttons that will explain").
 *
 * The one home for an explanation a screen doesn't need in plain sight: a small
 * (i) beside a title. Tap or click opens a short bubble; tap outside, tap the
 * (i) again or Esc closes. The bubble is portaled to <body> with fixed
 * positioning, so it isn't clipped by cards, sheets or the tech app's
 * transformed panes, and it never scrolls the page. The visible circle is
 * small; the touch target around it is 44px.
 *
 * Rule of thumb for DESC-CLEAN-01: live facts stay visible (status, who/when,
 * amounts); how-it-works and why go in an InfoTip; anything that repeats the
 * title or the button goes away.
 */
import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/cn";

export function InfoTip({
  children,
  label = "What this means",
  align = "start",
  className,
}: {
  /** The explanation — a sentence or two. */
  children: ReactNode;
  /** Screen-reader name for the button, e.g. "Where this payment goes". */
  label?: string;
  /** Which edge of the (i) the bubble lines up with. */
  align?: "start" | "end";
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const btn = useRef<HTMLButtonElement>(null);
  const pop = useRef<HTMLDivElement>(null);
  const id = useId();

  useEffect(() => {
    if (!open) return;
    const place = () => {
      const r = btn.current?.getBoundingClientRect();
      if (!r) return;
      const width = Math.min(300, window.innerWidth - 24);
      const want = align === "end" ? r.right - width + 8 : r.left - 10;
      const left = Math.max(12, Math.min(want, window.innerWidth - width - 12));
      const below = r.bottom + 8;
      // Flip above when there's no room under the (i).
      const top = below + 120 > window.innerHeight && r.top > 140 ? Math.max(12, r.top - 8 - 110) : below;
      setPos({ top, left, width });
    };
    place();
    const outside = (e: PointerEvent) => {
      const t = e.target as Node;
      if (pop.current?.contains(t) || btn.current?.contains(t)) return;
      setOpen(false);
    };
    const key = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        setOpen(false);
        btn.current?.focus();
      }
    };
    window.addEventListener("pointerdown", outside, true);
    window.addEventListener("keydown", key, true);
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("pointerdown", outside, true);
      window.removeEventListener("keydown", key, true);
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open, align]);

  return (
    <>
      <button
        ref={btn}
        type="button"
        aria-label={label}
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        className={cn(
          "relative inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full border bg-white align-middle",
          "font-serif text-[11px] font-bold italic leading-none [touch-action:manipulation]",
          "before:absolute before:-inset-[13px] before:content-['']",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40",
          open ? "border-slate-500 text-slate-800" : "border-slate-300 text-slate-500 hover:border-slate-400 hover:text-slate-700",
          className,
        )}
      >
        i
      </button>
      {open && pos &&
        createPortal(
          <div
            ref={pop}
            id={id}
            role="tooltip"
            // Inside a window (Radix Dialog) the page behind is click-through and a
            // press outside the window closes it. The bubble lives on <body>, so it
            // takes its own clicks and is marked as part of the window
            // ([data-stay-open], which Modal already honors).
            data-stay-open=""
            style={{ position: "fixed", top: pos.top, left: pos.left, width: pos.width, zIndex: 90, pointerEvents: "auto" }}
            className="rounded-xl bg-slate-900 px-3.5 py-2.5 text-left text-[12.5px] font-medium normal-case leading-snug tracking-normal text-white shadow-[0_12px_32px_-12px_rgba(15,23,42,0.6)]"
          >
            {children}
          </div>,
          document.body,
        )}
    </>
  );
}
